import React, { useMemo, useState } from 'react';
import { SelectInput, type Option } from '@plataforma-xvia/design-system-es';
import { CatalogDetail } from '../../../apis/detail-catalog';
import { SectionDetails } from './section-details';
import styles from '../styles.module.css';
import { LocationOnOutlined } from '@mui/icons-material';

interface LocationSectionProps {
  departmentSections: CatalogDetail['departmentSections'];
  selectedCity: string | undefined;
  selectedSection: string | undefined;
  onCityChange: (value: string) => void;
  onSectionChange: (value: string) => void;
}

export const LocationSection: React.FC<LocationSectionProps> = ({
  departmentSections,
  selectedCity,
  selectedSection,
  onCityChange,
  onSectionChange,
}) => {
  const [citySelectOpen, setCitySelectOpen] = useState(false);
  const [unitSelectOpen, setUnitSelectOpen] = useState(false);

  const normalizeCityValue = (city?: string | null) =>
    city?.trim() ?? '';

  const normalizedSelectedCity = normalizeCityValue(selectedCity);

  const cityOptions: Option[] = useMemo(
    () =>
      Array.from(
        new Set(
          (departmentSections || []).map((section) =>
            normalizeCityValue(section.city)
          )
        )
      ).map((city) => ({
        value: city,
        label: city || 'Não informado',
      })),
    [departmentSections]
  );

  const filteredSections = useMemo(
    () =>
      selectedCity !== undefined
        ? departmentSections?.filter(
            (section) => normalizeCityValue(section.city) === normalizedSelectedCity
          )
        : [],
    [departmentSections, normalizedSelectedCity, selectedCity]
  );

  const unitOptions: Option[] = useMemo(
    () =>
      filteredSections?.map((section) => {
        const sectionValue = section.id ?? section.name ?? '';
        return {
          value: sectionValue,
          label: section.name || 'Não informado',
        };
      }) || [],
    [filteredSections]
  );

  const selectedCityOption = cityOptions.find(
    (option) => option.value === normalizedSelectedCity
  ) || null;
  const selectedUnitOption = unitOptions.find(
    (option) => option.value === selectedSection
  ) || null;

  const handleCitySelectChange = (option: Option | null) => {
    onCityChange(option?.value || '');
    onSectionChange('');
  };

  const handleUnitSelectChange = (option: Option | null) => {
    onSectionChange(option?.value || '');
  };

  return (
    <div className={styles.locationSection}>
      <div className={styles.locationHeader}>
        <LocationOnOutlined />
        <div className={styles.locationTitle}>Onde realizar</div>
      </div>
      <div className={styles.locationDivider} />
      <div className={styles.locationInputs}>
        <div className={styles.inputGroup}>
          <SelectInput
            label="Selecione o município de atendimento"
            placeholder="Selecione..."
            options={cityOptions}
            value={selectedCityOption}
            setValue={handleCitySelectChange}
            isOpen={citySelectOpen}
            setIsOpen={setCitySelectOpen}
            filterable
            leadingIcon={null}
            className={styles.selectWrapper}
            labelClassName={styles.selectLabel}
            inputClassName={styles.selectTrigger}
        />
        </div>
        <div className={styles.inputGroup}>
          <SelectInput
            label="Selecione a unidade de atendimento"
            placeholder="Selecione..."
            options={unitOptions}
            value={selectedUnitOption}
            setValue={handleUnitSelectChange}
            isOpen={unitSelectOpen}
            setIsOpen={setUnitSelectOpen}
            disabled={!selectedCity}
            filterable
            leadingIcon={null}
            className={styles.selectWrapper}
            labelClassName={styles.selectLabel}
            inputClassName={styles.selectTrigger}
        />
        </div>
      </div>
      {selectedSection && (
        <div className={styles.sectionDetails}>
          {filteredSections?.map((section) => {
            const sectionValue = section.id ?? section.name ?? '';
            if (sectionValue === selectedSection) {
              return <SectionDetails key={section.id} section={section} />;
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};
